// Actions
import { ActionType } from "@ms/uno-actions/lib/local/ActionType";
// Constants
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { StoreEntityStatus, StoreTableNames } from "@ms/uno-constants/lib/local/StoreConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Entity
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
import { BasicTable } from "@ms/uno-entitycore/lib/local/tables/BasicTable";
import { EntityTable } from "@ms/uno-entitycore/lib/local/tables/EntityTable";
import { BucketSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyBuckets/BucketsSerializationHandler";
import { StoreEntityStatusSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyStatus/StoreEntityStatusSerializationHandler";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
// Utilities
import { upsertVersionedBaseline } from "../../../utilities/StoreUtilities";
import isEqual from "lodash/isEqual";
import { logUnreachableAction } from "../../../utilities/LoggingUtilities";
import { getStatusBasedOnResponse } from "../../../utilities/StatusUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
/**
 * Implementation of IBasicBucketStore
 */ export class BasicBucketStore extends EventPublishingStore {
    get name() {
        return "BasicBucketStore";
    }
    getBucket(id, readBaseline = false) {
        const bucket = this.bucketTable.get(id);
        const status = this.bucketStatusTable.get(id) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isBucketFetchingInProgress[id] ?? false;
        return {
            data: readBaseline ? bucket?.baseline ?? null : bucket?.value ?? null,
            status: status,
            isFetching
        };
    }
    getBucketsForPlan(planId, readBaseline = false) {
        const bucketsInPlan = this.bucketTable.getByIndex({
            name: "plan",
            value: planId
        });
        const status = this.bucketsInPlanStatusTable.get(planId) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isBucketsForPlanFetchingInProgress[planId] ?? false;
        return {
            data: Object.values(bucketsInPlan).map((bucket)=>readBaseline ? bucket.baseline : bucket.value),
            status: status,
            isFetching
        };
    }
    getPersistedTables() {
        return {
            [StoreTableNames.Buckets]: this.bucketTable,
            [StoreTableNames.BucketsInPlanStatus]: this.bucketsInPlanStatusTable
        };
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchBasicBucketAction:
                storeChanged = this.handleFetchBasicBucketAction(action);
                break;
            case ActionType.FetchBasicBucketFailedAction:
                storeChanged = this.handleFetchBasicBucketFailedAction(action);
                break;
            case ActionType.FetchBasicBucketSucceededAction:
                storeChanged = this.handleFetchBasicBucketSucceededAction(action);
                break;
            case ActionType.FetchBasicBucketsForPlanAction:
                storeChanged = this.handleFetchBasicBucketsForPlanAction(action);
                break;
            case ActionType.FetchBasicBucketsForPlanFailedAction:
                storeChanged = this.handleFetchBasicBucketsForPlanFailedAction(action);
                break;
            case ActionType.FetchBasicBucketsForPlanSucceededAction:
                storeChanged = this.handleFetchBasicBucketsForPlanSucceededAction(action);
                break;
            case ActionType.CreateBasicBucketAction:
                storeChanged = this.handleCreateBasicBucketAction(action);
                break;
            case ActionType.CreateBasicBucketFailedAction:
                storeChanged = this.handleCreateBasicBucketFailedAction(action);
                break;
            case ActionType.CreateBasicBucketSucceededAction:
                storeChanged = this.handleCreateBasicBucketSucceededAction(action);
                break;
            case ActionType.DeltaSyncCreateBasicBucketAction:
                storeChanged = this.handleDeltaSyncCreateBasicBucketAction(action);
                break;
            case ActionType.DeleteBasicBucketAction:
                storeChanged = this.handleDeleteBasicBucketAction(action);
                break;
            case ActionType.DeleteBasicBucketFailedAction:
                storeChanged = this.handleDeleteBasicBucketFailedAction(action);
                break;
            case ActionType.DeleteBasicBucketSucceededAction:
                storeChanged = this.handleDeleteBasicBucketSucceededAction(action);
                break;
            case ActionType.DeltaSyncDeleteBasicBucketAction:
                storeChanged = this.handleDeltaSyncDeleteBasicBucketAction(action);
                break;
            case ActionType.UpdateBasicBucketAction:
                storeChanged = this.handleUpdateBasicBucketAction(action);
                break;
            case ActionType.UpdateBasicBucketFailedAction:
                storeChanged = this.handleUpdateBasicBucketFailedAction(action);
                break;
            case ActionType.UpdateBasicBucketSucceededAction:
                storeChanged = this.handleUpdateBasicBucketSucceededAction(action);
                break;
            case ActionType.DeltaSyncUpdateBasicBucketAction:
                storeChanged = this.handleDeltaSyncUpdateBasicBucketAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleFetchBasicBucketAction(action) {
        if (this.isBucketFetchingInProgress[action.bucketId]) {
            return false;
        }
        this.isBucketFetchingInProgress[action.bucketId] = true;
        return true;
    }
    handleFetchBasicBucketFailedAction(action) {
        let storeChanged = false;
        const { response, bucketId } = action;
        if (this.isBucketFetchingInProgress[bucketId]) {
            this.isBucketFetchingInProgress[bucketId] = false;
            storeChanged = true;
        }
        // Update the status if appropriate
        if (response?.status === HttpStatus.NotFound || response?.status === HttpStatus.Forbidden) {
            storeChanged = this.updateBucketStatusBasedOnResponse(bucketId, response) || storeChanged;
        } else {
            this.bucketStatusTable.set(bucketId, StoreEntityStatus.UnableToFetch);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleFetchBasicBucketSucceededAction(action) {
        return this.upsertBucket(action.bucket);
    }
    handleFetchBasicBucketsForPlanAction(action) {
        if (this.isBucketsForPlanFetchingInProgress[action.planId]) {
            return false;
        }
        this.isBucketsForPlanFetchingInProgress[action.planId] = true;
        return true;
    }
    handleFetchBasicBucketsForPlanFailedAction(action) {
        let storeChanged = false;
        const { response, planId } = action;
        const planBucketsIndexStatus = this.bucketsInPlanStatusTable.get(planId);
        if (this.isBucketsForPlanFetchingInProgress[planId]) {
            this.isBucketsForPlanFetchingInProgress[planId] = false;
            storeChanged = true;
        }
        // Update the status if appropriate
        if (response?.status === HttpStatus.NotFound || response?.status === HttpStatus.Forbidden) {
            if (response?.status === HttpStatus.NotFound && planBucketsIndexStatus !== StoreEntityStatus.NotFound) {
                this.bucketsInPlanStatusTable.set(planId, StoreEntityStatus.NotFound);
                storeChanged = true;
            } else if (response?.status === HttpStatus.Forbidden && planBucketsIndexStatus !== StoreEntityStatus.Inaccessible) {
                this.bucketsInPlanStatusTable.set(planId, StoreEntityStatus.Inaccessible);
                storeChanged = true;
            }
        } else {
            this.bucketsInPlanStatusTable.set(planId, StoreEntityStatus.UnableToFetch);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleFetchBasicBucketsForPlanSucceededAction(action) {
        let storeChanged = false;
        if (this.isBucketsForPlanFetchingInProgress[action.planId]) {
            this.isBucketsForPlanFetchingInProgress[action.planId] = false;
            storeChanged = true;
        }
        for (const bucket of action.buckets){
            storeChanged = this.upsertBucket(bucket) || storeChanged;
        }
        if (this.configProvider().flights.EnableTasksDataPersistence) {
            const bucketIdsInPlan = Object.values(this.bucketTable.getByIndex({
                name: "plan",
                value: action.planId
            })).map((bucket)=>bucket.baseline.id);
            const allBucketIds = action.buckets.map((bucket)=>bucket.id);
            for (const bucketId of bucketIdsInPlan){
                if (!allBucketIds.includes(bucketId) && !LocalIdGenerator.isLocalTempId(bucketId)) {
                    storeChanged = this.bucketTable.delete(bucketId) || storeChanged;
                }
            }
        }
        storeChanged = this.bucketsInPlanStatusTable.set(action.planId, StoreEntityStatus.Present) || storeChanged;
        return storeChanged;
    }
    handleCreateBasicBucketAction(action) {
        const { bucket } = action;
        this.addBucketAndStatus(bucket);
        return true;
    }
    handleCreateBasicBucketFailedAction(action) {
        const { bucket, response } = action;
        const storeCopy = this.bucketTable.get(bucket.id);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e24958f /* tag_4jjwp */ , TraceLevel.Warning, `BucketNotFoundInStore [Action=${action.type}][BucketId=${bucket.id}]`);
            return false;
        }
        // Remove from the store tables
        this.deleteBucketAndStatus(storeCopy.baseline.id);
        // Update the status if appropriate
        if (response != null) {
            this.updateBucketStatusBasedOnResponse(bucket.id, response);
        }
        return true;
    }
    handleCreateBasicBucketSucceededAction(action) {
        const { bucket, localId } = action;
        const storeCopy = this.bucketTable.get(localId);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e24958e /* tag_4jjwo */ , TraceLevel.Warning, `BucketNotFoundInStore [Action=${action.type}][BucketId=${localId}]`);
            return false;
        }
        // Update the store tables with the server copy
        storeCopy.updateBaseline(bucket);
        this.bucketTable.reKey(localId, bucket.id);
        this.bucketStatusTable.set(bucket.id, StoreEntityStatus.Present);
        // Remove the local copy from the store tables
        this.deleteBucketAndStatus(localId);
        return true;
    }
    handleDeltaSyncCreateBasicBucketAction(action) {
        const { bucket } = action;
        const storeCopy = this.bucketTable.get(bucket.id);
        if (storeCopy != null) {
            this.loggers.traceLogger.logTrace(0x1e24958d /* tag_4jjwn */ , TraceLevel.Info, `StoreException: Basic bucket already exists in store [BucketId=${bucket.id}]`);
            return false;
        }
        this.addBucketAndStatus(bucket);
        return true;
    }
    handleDeleteBasicBucketAction(action) {
        const { bucketId } = action;
        const storeCopy = this.bucketTable.get(bucketId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e24958c /* tag_4jjwm */ , TraceLevel.Warning, `BucketNotFoundInStore [Action=${action.type}][BucketId=${bucketId}]`);
            return false;
        }
        // No need to delete if already soft deleted.
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e24958b /* tag_4jjwl */ , TraceLevel.Warning, `StoreException: Basic bucket already soft deleted in store [BucketId=${bucketId}]`);
            return false;
        }
        // Soft delete the bucket
        storeCopy.softDelete();
        this.bucketStatusTable.set(bucketId, StoreEntityStatus.SoftDeleted);
        return true;
    }
    handleDeleteBasicBucketFailedAction(action) {
        const { deletedBucket, response } = action;
        const storeCopy = this.bucketTable.get(deletedBucket.id);
        if (!storeCopy) {
            /**
             * This can happen if a delta sync delete occurs before the
             * response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e24958a /* tag_4jjwk */ , TraceLevel.Warning, `BucketNotFoundInStore [Action=${action.type}][BucketId=${deletedBucket.id}]`);
            return false;
        }
        let storeChanged = false;
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            // Un-delete the bucket
            storeCopy.undelete();
            this.bucketStatusTable.set(deletedBucket.id, StoreEntityStatus.Present);
            storeChanged = true;
        } else {
            this.loggers.traceLogger.logTrace(0x1e249589 /* tag_4jjwj */ , TraceLevel.Warning, `StoreException: Basic Bucket deletion failure for non soft-deleted bucket [BucketId=${deletedBucket.id}]`);
        }
        // Update the status if appropriate
        if (response != null) {
            storeChanged = this.updateBucketStatusBasedOnResponse(deletedBucket.id, response);
        }
        return storeChanged;
    }
    handleDeleteBasicBucketSucceededAction(action) {
        const { bucketId } = action;
        const storeCopy = this.bucketTable.get(bucketId);
        if (storeCopy == null) {
            // Log this case. This can happen in the rare chance if a delta sync returns before the response of the delete
            const msg = `BucketNotFoundInStore [BucketId=${bucketId}]`;
            this.loggers.traceLogger.logTrace(0x1e249588 /* tag_4jjwi */ , TraceLevel.Info, msg);
            return false;
        }
        this.deleteBucketAndStatus(bucketId);
        return true;
    }
    handleDeltaSyncDeleteBasicBucketAction(action) {
        const { bucketId } = action;
        const storeCopy = this.bucketTable.get(bucketId);
        if (storeCopy == null) {
            // This can happen in the case where a local delete occurs before a delta sync based delete.
            // Log this case with a warning because the feed processor should be ignoring this delta.
            this.loggers.traceLogger.logTrace(0x1e249587 /* tag_4jjwh */ , TraceLevel.Warning, `BucketNotFoundInStore [BucketId=${bucketId}]`);
            return false;
        }
        this.deleteBucketAndStatus(bucketId);
        return true;
    }
    handleUpdateBasicBucketAction(action) {
        let storeChanged = false;
        const { bucketId, delta, updateKey } = action;
        const storeCopy = this.bucketTable.get(bucketId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e249586 /* tag_4jjwg */ , TraceLevel.Warning, `BucketNotFoundInStore [Action=${action.type}][BucketId=${bucketId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e249585 /* tag_4jjwf */ , TraceLevel.Warning, `StoreException: Basic bucket soft deleted in store [BucketId=${bucketId}]`);
            return false;
        }
        const updatedBucket = storeCopy.value.getClone().applyDiffs(delta);
        if (!isEqual(storeCopy.value, updatedBucket)) {
            this.loggers.traceLogger.logTrace(0x1e249584 /* tag_4jjwe */ , TraceLevel.Verbose, `Pushing delta for Basic bucket [BucketId=${bucketId}][UpdateKey=${updateKey}][DeltaCount=${storeCopy.deltaCount}]`);
            storeCopy.pushDelta(delta, updateKey);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleUpdateBasicBucketFailedAction(action) {
        let storeChanged = false;
        const { bucketId, response, updateKey } = action;
        const storeCopy = this.bucketTable.get(bucketId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This can happen if a local delete or delta sync delete occurs before the response of the update returns. Log a warning and no-op
            this.loggers.traceLogger.logTrace(0x1e249583 /* tag_4jjwd */ , TraceLevel.Warning, `BucketNotFoundInStore [BucketId=${bucketId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Revert the delta if it exists
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.revertDelta(updateKey);
            storeChanged = true;
        }
        // Update the status if necessary
        if (response != null) {
            storeChanged = this.updateBucketStatusBasedOnResponse(bucketId, response) || storeChanged;
        }
        return storeChanged;
    }
    handleUpdateBasicBucketSucceededAction(action) {
        let storeChanged = false;
        const { updatedBucket, updateKey } = action;
        const storeCopy = this.bucketTable.get(updatedBucket.id);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This can happen if a local delete or delta sync delete occurs before the response of the update returns. Log a warning and no-op
            this.loggers.traceLogger.logTrace(0x1e249582 /* tag_4jjwc */ , TraceLevel.Warning, `BucketNotFoundInStore [BucketId=${updatedBucket.id}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Commit the pending update
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.commitDelta(updateKey, updatedBucket);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleDeltaSyncUpdateBasicBucketAction(action) {
        let storeChanged = false;
        const { updatedBucket } = action;
        const storeCopy = this.bucketTable.get(updatedBucket.id);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e249581 /* tag_4jjwb */ , TraceLevel.Warning, `BucketNotFoundInStore [Action=${action.type}][BucketId=${updatedBucket.id}]`);
            return false;
        }
        storeChanged = this.upsertBucket(updatedBucket, false) || storeChanged;
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This is an indicator that the out going delete request will fail from a delete conflict. Log a warning and only update the baseline if needed
            this.loggers.traceLogger.logTrace(0x1e249580 /* tag_4jjwa */ , TraceLevel.Warning, `StoreException: Basic bucket soft deleted in store [BucketId=${updatedBucket.id}]`);
            return storeChanged;
        }
        return storeChanged;
    }
    /**
     * Add a bucket to the store tables
     * @param bucket Basic bucket to add
     */ addBucketAndStatus(bucket) {
        this.bucketTable.add(bucket.id, bucket);
        this.bucketStatusTable.set(bucket.id, StoreEntityStatus.Present);
    }
    /**
     * Delete a bucket from the store tables
     * @param bucketId Id of the Basic bucket
     */ deleteBucketAndStatus(bucketId) {
        this.bucketTable.delete(bucketId);
        this.bucketStatusTable.delete(bucketId);
    }
    /**
     * Upsert a bucket into the store
     * @param bucket Bucket to upsert
     * @param markPresent Whether to mark the bucket as present
     */ upsertBucket(bucket, markPresent = true) {
        let storeChanged = false;
        if (this.isBucketFetchingInProgress[bucket.id]) {
            this.isBucketFetchingInProgress[bucket.id] = false;
            storeChanged = true;
        }
        storeChanged = upsertVersionedBaseline(this.bucketTable, bucket.id, bucket) || storeChanged;
        if (markPresent) {
            storeChanged = this.bucketStatusTable.set(bucket.id, StoreEntityStatus.Present) || storeChanged;
        }
        return storeChanged;
    }
    /**
     * Update the status of a bucket based on the request response
     * @param bucketId Id of the Basic bucket
     * @param response Response from the request
     */ updateBucketStatusBasedOnResponse(bucketId, response) {
        const status = getStatusBasedOnResponse(response);
        if (status) {
            return this.bucketStatusTable.set(bucketId, status);
        }
        return false;
    }
    constructor(configProvider, loggers){
        super(), this.configProvider = configProvider, this.loggers = loggers, this.bucketTable = new EntityTable(StoreTableNames.Buckets, {
            plan: (bucket)=>bucket.planId
        }, new BucketSerializationHandler()), this.bucketStatusTable = new BasicTable(StoreTableNames.BucketStatus), this.bucketsInPlanStatusTable = new BasicTable(StoreTableNames.BucketsInPlanStatus, {}, new StoreEntityStatusSerializationHandler()), this.isBucketFetchingInProgress = {}, this.isBucketsForPlanFetchingInProgress = {};
    }
}
